import axios, { create } from 'axios';
import { isObjectLike } from 'lodash';
import config from '../../config/appConfig';
import authHelper from 'helpers/authHelper';
import logger from 'helpers/logger';
import localStorageHelper from 'helpers/localStorageHelper';
import { processQueue, isUserSessionActive } from '../helpers';
import { getEndpointHeaders, getUserHeaders } from './headerMapper';
import { GLOBAL_ERROR_STATUS_CODES, SESSION_ERROR_STATUS_CODES, AUTHORIZATION_HEADER, BEARER_PREFIX, GRANT_TYPE_REFRESH_TOKEN, DEFAULT_SCOPE, ERROR_MISSING_RESPONSE, CONTENT_TYPE_FORM_URLENCODED, TOKEN_ENDPOINT } from './constants';
import { history } from '../../boot-client';

let isRefreshing = false;
let failedQueue = [];

const refreshAccessToken = async () => {
    try {
        const { openIDIssuer, openIDClientID } = localStorageHelper.nonEncrypted.getItem('environmentProperties') || {};
        const authTokens = authHelper.getAccessTokenData();

        if (!authTokens?.refresh_token) {
            throw new Error('No refresh token available');
        }

        const params = new URLSearchParams();
        params.append('grant_type', GRANT_TYPE_REFRESH_TOKEN);
        params.append('scope', authTokens?.scope || DEFAULT_SCOPE);
        params.append('refresh_token', authTokens?.refresh_token);
        params.append('client_id', openIDClientID);

        const response = await axios.post(`${openIDIssuer}/${TOKEN_ENDPOINT}`, params, {
            headers: {
                'Content-Type': CONTENT_TYPE_FORM_URLENCODED
            }
        });
        if (response?.data?.access_token) {
            const newTokenData = {
                ...authTokens,
                ...response.data,
            };

            localStorageHelper.nonEncrypted.setItem(
                `authenticated_user:${openIDIssuer}:${openIDClientID}`,
                newTokenData
            );

            return newTokenData;
        }

        throw new Error('Invalid token response');
    } catch (error) {
        logger.error('Token refresh failed:', error);
        throw error;
    }
};

//Get user specific headers
export const addUserHeaders = (request) => {
    const originalRequest = request;
    const accessToken = authHelper.getAccessToken();
    const user = localStorageHelper.getItem('user');

    // Axios 1.x+ way to set headers - merge into headers directly
    const userHeaders = getUserHeaders(user, accessToken);
    Object.assign(originalRequest.headers, userHeaders);

    return originalRequest;
};


function getInstance(baseUrl) {
    const instance = create({
        baseURL: baseUrl,
        timeout: 180000
    });

    instance.interceptors.request.use(addUserHeaders,
        (error) => {
            logger.setLogToStorage(error);
            return Promise.reject(error);
        });

    instance.interceptors.response.use(
        (result) => {
            if (result?.data?.response) {
                return result.data.response;
            }
            if (result?.data) {
                return result.data;
            }
            else {
                const error = {};
                error.message = ERROR_MISSING_RESPONSE;
                logger.setLogToStorage(error);
            }
        },
        async (error) => {
            const originalRequest = error?.config;
            const response = error?.response;

            if (!error?.statusCode) {
                error.statusCode = response?.status;
            }
            if (response?.data) {
                const respData = isObjectLike(response.data) ? response.data : { message: response.data };
                error = {
                    ...error,
                    ...respData
                };
            }

            if (SESSION_ERROR_STATUS_CODES.indexOf(error.statusCode) !== -1) {

                if (originalRequest?._retry) {
                    logger.setLogToStorage('Token refresh failed - logging out user');
                    authHelper.forceLogoutUser();
                    authHelper.redirectToLogin(history.location.pathname);
                    throw error;
                }

                if (!isUserSessionActive(localStorageHelper, config)) {
                    logger.setLogToStorage('Unauthorized user or expired token');
                    authHelper.forceLogoutUser();
                    authHelper.redirectToLogin(history.location.pathname);
                    throw error;
                }

                originalRequest._retry = true;

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    })
                        .then(token => {
                            originalRequest.headers[AUTHORIZATION_HEADER] = `${BEARER_PREFIX} ${token}`;
                            return instance(originalRequest);
                        })
                        .catch(err => {
                            throw err;
                        });
                }

                isRefreshing = true;

                try {
                    const newTokens = await refreshAccessToken();

                    instance.defaults.headers.common[AUTHORIZATION_HEADER] = `${BEARER_PREFIX} ${newTokens?.access_token}`;
                    originalRequest.headers[AUTHORIZATION_HEADER] = `${BEARER_PREFIX} ${newTokens?.access_token}`;

                    processQueue(failedQueue, null, newTokens?.access_token);
                    failedQueue = [];
                    isRefreshing = false;

                    return instance(originalRequest);

                } catch (refreshError) {
                    processQueue(failedQueue, refreshError, null);
                    failedQueue = [];
                    isRefreshing = false;

                    logger.setLogToStorage('Refresh token expired - logging out user');
                    authHelper.forceLogoutUser();
                    authHelper.redirectToLogin(history.location.pathname);

                    throw refreshError;
                }
            }
            else if (GLOBAL_ERROR_STATUS_CODES.indexOf(error.statusCode) !== -1) {
                logger.setLogToStorage('Global ERROR: ' + error.statusCode);
                throw error;
            }
            else {
                logger.error(error);
                logger.setLogToStorage(error);
                throw error;
            }
        }
    );

    return instance;
}

class RestManager {
    constructor(instance) {
        this.instance = instance;
    }

    async get(endpoint) {
        return await this.instance.get(endpoint.url, { headers: getEndpointHeaders(endpoint) });
    }

    async post(endpoint, params) {
        return await this.instance.post(endpoint.url, params, { headers: getEndpointHeaders(endpoint) });
    }

    async patch(endpoint, params) {
        return await this.instance.patch(endpoint.url, params, { headers: getEndpointHeaders(endpoint) });
    }

    async put(endpoint, params) {
        return await this.instance.put(endpoint.url, params, { headers: getEndpointHeaders(endpoint) });
    }

    async delete(endpoint, data) {
        const headers = getEndpointHeaders(endpoint);

        return await this.instance.delete(endpoint.url, { headers, data });
    }

    async putBlob(endpoint, params) {
        return await this.instance.put(endpoint.url, params, { headers: { ...getEndpointHeaders(endpoint), 'x-ms-blob-type': 'BlockBlob' } });
    }

    async postBlob(endpoint, params) {
        return await this.instance.post(endpoint.url, params, { headers: getEndpointHeaders(endpoint), responseType: 'blob' });
    }
}

//Create multiple instances to handle different basepath for different endpoints.
// Eg. microservices might not have apiBasePath
// but gray backend might still use "pt-api" as a prefix

export const RestManagerWithoutBaseUrl = new RestManager(getInstance(config.apiUrlWithoutBase));
export const RestManagerWithSmartSensorUrl = new RestManager(getInstance(config.apiUrlWithSmartSensor));
